import { service } from '@ember/service';
import ApplicationSerializer from 'admin-panel/serializers/application';

import type ModelRegistry from 'ember-data/types/registries/model'; // eslint-disable-line ember/use-ember-data-rfc-395-imports
import type DS from 'ember-data'; // eslint-disable-line ember/use-ember-data-rfc-395-imports
import type StoreService from '@ember-data/store';
import type PageModel from 'admin-panel/models/page';
import type SessionInfoService from 'admin-panel/services/session-info';

type PagePayload = {
  id?: string | number;
  title?: string;
  content?: string | null;
  title_translations?: Record<string, string>;
  content_translations?: Record<string, string>;
};

type PageResponsePayload = {
  page?: PagePayload;
  pages?: PagePayload[];
  meta?: {
    locale?: string;
  };
};

type SerializedPagePayload = PagePayload & Record<string, unknown>;

export default class PageSerializer extends ApplicationSerializer {
  @service
  declare sessionInfo: SessionInfoService;

  normalizeResponse(
    ...args: Parameters<ApplicationSerializer['normalizeResponse']>
  ) {
    const store = args[0] as StoreService;
    const payload = args[2] as PageResponsePayload;
    const localeKey =
      payload.meta?.locale ?? this.sessionInfo.contentLocale.key;

    this.normalizeLocalizedPage(payload.page, localeKey, store);
    payload.pages?.forEach((page) => {
      this.normalizeLocalizedPage(page, localeKey, store);
    });

    return super.normalizeResponse(...args);
  }

  serialize<K extends keyof ModelRegistry>(
    snapshot: DS.Snapshot<K>,
    options?: Record<string, unknown>
  ) {
    const json = super.serialize(
      snapshot,
      options ?? {}
    ) as SerializedPagePayload;
    const localeKey = this.sessionInfo.contentLocale.key;

    if (json.title_translations) {
      const title = json.title_translations[localeKey] ?? '';

      json.title = title;
      delete json.title_translations;
    }

    if (json.content_translations) {
      const content = json.content_translations[localeKey] ?? '';

      json.content = content;
      delete json.content_translations;
    }

    if (snapshot.id !== null) {
      delete json['category_id'];
      delete json['hotel_id'];
    }

    return json;
  }

  private normalizeLocalizedPage(
    page: PagePayload | undefined,
    localeKey: string,
    store: StoreService
  ) {
    if (!page) {
      return;
    }

    const existingPage = existingPageFor(page, store);

    if (typeof page.title === 'string') {
      page.title_translations = {
        ...stringTranslations(existingPage?.titleTranslations),
        ...(page.title_translations ?? {}),
        [localeKey]: page.title,
      };
    }

    if (typeof page.content === 'string' || page.content === null) {
      page.content_translations = {
        ...stringTranslations(existingPage?.contentTranslations),
        ...(page.content_translations ?? {}),
        [localeKey]: page.content ?? '',
      };
    }
  }
}

function existingPageFor(page: PagePayload, store: StoreService) {
  if (page.id === undefined || page.id === null) {
    return null;
  }

  return store.peekRecord('page', String(page.id)) as PageModel | null;
}

function stringTranslations(
  translations: Record<string, string | null> | undefined
) {
  const stringValues: Record<string, string> = {};

  Object.entries(translations ?? {}).forEach(([localeKey, value]) => {
    if (typeof value === 'string') {
      stringValues[localeKey] = value;
    }
  });

  return stringValues;
}

declare module 'ember-data/types/registries/serializer' {
  export default interface SerializerRegistry {
    page: PageSerializer;
  }
}
